<template>
  <div>
    <div v-for="(kpi, index) in kpiProgress" :key="kpi.kpiId" class="p-1">
      <div class="d-flex align-items-center justify-content-between mb-1">
        <h5>KPI #{{ index + 1 }}</h5>
        <div class="d-flex align-items-center" style="gap: 1rem;">
          <h5>KPI total: {{ kpi.kpiTotal }}</h5>
          <b-button
            variant="primary"
            size="sm"
            @click="fetchKpiTrips(kpi.kpiId, index)"
          >
            {{ expandedKpi === index ? "Hide Trips" : "Show Trips" }}
          </b-button>
        </div>
      </div>
      <b-table
        :items="getDriverData(kpi)"
        :fields="fields"
        responsive
        bordered
        small
        show-empty
        empty-text="No Data For this KPI"
      >
        <template #cell(progress)="data"> {{ data.item.progress }}% </template>
        <template #cell(total)="data">
          {{ data.item.total }}
        </template>
        <template #cell(name)="data">
          <div class="d-flex align-items-center">
            <span v-if="isMember(data.item)">
              {{ data.item.name }}
            </span>
            <span v-else>
              Driver Left
              <b-button size="sm" variant="link" @click="copyId(data.item.id)">
                (Copy ID)
              </b-button>
            </span>
          </div>
        </template>
      </b-table>

      <!-- Trips Table Section -->
      <div v-if="expandedKpi === index" class="mt-2">
        <base-overlay :show="loadingTrips">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <h5 class="mb-0">
              KPI Trips
            </h5>
            <h6 class="mb-0">Total Trips: {{ totalKpiTrips }}</h6>
          </div>
          <b-table
            :items="kpiTripsList"
            :fields="tripsTableColumns"
            responsive
            striped
            hover
            bordered
            show-empty
            empty-text="No trips found for this KPI"
            class="text-center"
          >
            <template #cell(driver)="data">
              <span
                v-if="data.item.driver && data.item.driver.first_name"
                class="cursor-pointer text-primary"
                style="cursor: pointer;"
                @click="openDriverProfile(data.item.driver)"
              >
                {{ data.item.driver.first_name }}
                {{ data.item.driver.last_name }}
              </span>
              <span v-else>-</span>
            </template>
            <template #cell(state)="data">
              <trip-status-badge :status="data.item.state" />
            </template>
            <template #cell(tripLink)="data">
              <b-button
                variant="flat-primary"
                size="sm"
                @click="openTripDetails(data.item.id)"
              >
                <feather-icon icon="ExternalLinkIcon" />
                View Trip
              </b-button>
            </template>
          </b-table>
          <base-pagination
            v-model="tripsQueryParams.page"
            :total="totalKpiTrips"
            @input="fetchKpiTrips(currentKpiId, expandedKpi, true)"
            @new-per-page="onPerPageChange(kpi.kpiId, index)"
          />
        </base-overlay>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BTable } from "bootstrap-vue";
import { ref } from "@vue/composition-api";
import {
  successNotification,
  errorNotification,
} from "@/utils/toast-notifications";
import axiosIns from "@/libs/axios";
import BaseOverlay from "@/UI/BaseOverlay.vue";
import BasePagination from "@/UI/BasePagination.vue";
import TripStatusBadge from "@/views/trips/components/TripStatusBadge.vue";

export default {
  components: {
    BTable,
    BButton,
    BaseOverlay,
    BasePagination,
    TripStatusBadge,
  },
  props: {
    kpiProgress: {
      type: Array,
      required: true,
    },
    teamMembers: {
      type: Array,
      required: true,
    },
    teamId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const fields = [
      { key: "name", label: "Driver Name" },
      { key: "progress", label: "Progress %" },
      { key: "total", label: "Completed Trips" },
    ];

    const tripsTableColumns = [
      {
        key: "number",
        label: "Trip Number",
      },
      {
        key: "driver",
        label: "Driver",
      },
      {
        key: "state",
        label: "Status",
      },
      {
        key: "tripLink",
        label: "Trip Link",
      },
    ];

    const expandedKpi = ref(null);
    const currentKpiId = ref(null);
    const loadingTrips = ref(false);
    const kpiTripsList = ref([]);
    const totalKpiTrips = ref(0);
    const tripsQueryParams = ref({
      page: 1,
      perPage: 10,
    });

    const isMember = (item) => props.teamMembers.some((m) => m.id === item.id);

    const copyId = (driverId) => {
      navigator.clipboard
        .writeText(driverId)
        .then(() => {
          successNotification("Driver ID copied to clipboard!");
        })
        .catch((err) => console.error(err));
    };

    const getDriverData = (kpi) => {
      const personalProgress = kpi.personalProgress || {};
      const personalTotal = kpi.personalTotal || {};

      return Object.keys({ ...personalProgress, ...personalTotal }).map(
        (driverId) => {
          const member = props.teamMembers.find((m) => m.id === driverId);
          return {
            id: driverId,
            name: member ? member.name : driverId,
            progress: personalProgress[driverId] ?? 0,
            total: personalTotal[driverId] ?? 0,
          };
        }
      );
    };

    const fetchKpiTrips = async (kpiId, index, isPagination = false) => {
      // Toggle expansion only if not from pagination
      if (!isPagination && expandedKpi.value === index) {
        expandedKpi.value = null;
        currentKpiId.value = null;
        return;
      }

      // If opening a new KPI, reset to page 1
      if (!isPagination && currentKpiId.value !== kpiId) {
        tripsQueryParams.value.page = 1;
      }

      expandedKpi.value = index;
      currentKpiId.value = kpiId;

      loadingTrips.value = true;

      try {
        const { page, perPage } = tripsQueryParams.value;
        const result = await axiosIns.get(
          `/teams/${props.teamId}/trips?kpiId=${kpiId}&page=${page}&perPage=${perPage}`
        );
        console.log("KPI Trips Result:", result.data);
        kpiTripsList.value = result.data.trips || [];
        totalKpiTrips.value = result.data.total || 0;
      } catch (error) {
        console.error("Error fetching KPI trips:", error);
        errorNotification("Failed to fetch trips");
        kpiTripsList.value = [];
        totalKpiTrips.value = 0;
      } finally {
        loadingTrips.value = false;
      }
    };

    const openDriverProfile = (driver) => {
      if (driver && driver.driver_id) {
        window.open(
          `/drivers/${driver.id}?externalId=${driver.driver_id}`,
          "_blank"
        );
      }
    };

    const openTripDetails = (tripId) => {
      window.open(`/trips/${tripId}`, "_blank");
    };

    const onPerPageChange = (kpiId, index) => (newPerPage) => {
      tripsQueryParams.value.perPage = newPerPage;
      tripsQueryParams.value.page = 1;
      fetchKpiTrips(kpiId, index, true);
    };

    return {
      copyId,
      fields,
      isMember,
      getDriverData,
      fetchKpiTrips,
      expandedKpi,
      currentKpiId,
      loadingTrips,
      kpiTripsList,
      totalKpiTrips,
      tripsTableColumns,
      tripsQueryParams,
      openDriverProfile,
      openTripDetails,
      onPerPageChange,
    };
  },
};
</script>
